/* global SideDrawer */

if (!customElements.get('added-to-cart-drawer')) {
  class AddedToCartDrawer extends SideDrawer {
    constructor() {
      super();
      this.heading = this.querySelector('.js-added-to-cart-heading');
      this.body = this.querySelector('.js-added-to-cart-body');
      this.openHandler = this.handleOpen.bind(this);
    }

    connectedCallback() {
      document.addEventListener('dispatch:added-to-cart-drawer:open', this.openHandler);

      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:added-to-cart-drawer:open', this.openHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }
    }

    /**
     * Handles the open event dispatched by product-form after a successful add to cart.
     * @param {object} evt - Event object with detail { productId, lineKey, opener }.
     */
    handleOpen(evt) {
      const { productId, lineKey, opener } = evt.detail;
      this.show(productId, lineKey, opener);
    }

    /**
     * Opens the panel in the Theme Editor, previewing the first cart item.
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target !== this.closest('.shopify-section')) return;

      const { previewProductId, previewLineKey } = this.dataset;
      if (!previewProductId) {
        throw new Error('Added-to-cart panel: add a product to the cart to preview the panel in the theme editor.');
      }
      this.show(previewProductId, previewLineKey);
    }

    /**
     * Closes the panel when the section is deselected in the Theme Editor.
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Renders the panel content for the added line item and opens the panel.
     * @param {string|number} productId - Id of the added product (source for related recommendations).
     * @param {string} lineKey - Cart line item key of the added item.
     * @param {Element} [opener] - Element to return focus to when the panel closes.
     */
    async show(productId, lineKey, opener) {
      const content = await this.fetchContent(productId);
      this.heading.textContent = content.heading;
      this.body.innerHTML = content.body;
      this.revealLineItem(lineKey);

      if (!this.hasAttribute('open')) this.open(opener);
    }

    /**
     * Fetches the section rendered through the Product Recommendations endpoint.
     * @param {string|number} productId - Product id to get related products for.
     * @returns {Promise<{heading: string, body: string}>} Heading text and inner HTML for the panel body.
     */
    async fetchContent(productId) {
      const { recommendationsUrl, sectionId, limit } = this.dataset;
      const url = `${recommendationsUrl}?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=related`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Added-to-cart panel: failed to load content from ${url} (status ${response.status}).`);
      }

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();

      const heading = tmpl.content.querySelector('.js-added-to-cart-heading');
      const body = tmpl.content.querySelector('.js-added-to-cart-body');
      if (!heading || !body) {
        throw new Error('Added-to-cart panel: section response is missing ".js-added-to-cart-heading" or ".js-added-to-cart-body".');
      }
      return { heading: heading.textContent.trim(), body: body.innerHTML };
    }

    /**
     * Shows the rendered line item matching the given key.
     * @param {string} lineKey - Cart line item key.
     */
    revealLineItem(lineKey) {
      const item = Array.from(this.body.querySelectorAll('[data-line-key]'))
        .find((el) => el.dataset.lineKey === lineKey);
      if (!item) {
        throw new Error(`Added-to-cart panel: line item "${lineKey}" was not found in the cart.`);
      }
      item.hidden = false;
    }
  }

  customElements.define('added-to-cart-drawer', AddedToCartDrawer);
}
